import { BottomSheet } from '@/components/BottomSheet';
import { CheckIcon, ChevronDownIcon } from '@/components/icons';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { classNames } from '@/utils/classNames';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './DaysPickerDropdown.module.css';

const DAY_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 1);

type DaysPickerDropdownProps = {
  value: string;
  onChange: (value: string) => void;
};

export const DaysPickerDropdown = ({ value, onChange }: DaysPickerDropdownProps) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setLocalValue(value);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen, value]);

  useEffect(() => {
    if (isOpen && listRef.current && localValue) {
      const selectedElement = listRef.current.querySelector(`[data-value="${localValue}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
  }, [isOpen, localValue]);

  const handleTriggerClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleDaySelect = useCallback(
    (day: number) => {
      const dayStr = String(day);
      setLocalValue(dayStr);
      onChange(dayStr);
      setIsOpen(false);
    },
    [onChange]
  );

  const displayValue = value || 'Select days';

  const daysListContent = (
    <div className={styles.daysList} ref={listRef}>
      {DAY_OPTIONS.map((day) => (
        <button
          key={day}
          type="button"
          className={classNames(styles.dayItem, localValue === String(day) && styles.selected)}
          onClick={() => {
            handleDaySelect(day);
          }}
          data-value={day}
        >
          <span className={styles.dayLabel}>{day}</span>
          {localValue === String(day) && <CheckIcon className={styles.checkIcon} />}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={classNames(styles.trigger, isOpen && styles.open)}
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={classNames(!value && styles.placeholder)}>{displayValue}</span>
        <ChevronDownIcon className={classNames(styles.chevron, isOpen && styles.rotated)} />
      </button>

      {isMobile ? (
        <BottomSheet
          isOpen={isOpen}
          onClose={() => {
            setLocalValue(value);
            setIsOpen(false);
          }}
          title="Days of skiing"
        >
          {daysListContent}
        </BottomSheet>
      ) : (
        isOpen && <div className={styles.dropdown}>{daysListContent}</div>
      )}
    </div>
  );
};
